/**
 * Lead labels, shown in shadow next to WhatConverts' own mark.
 *
 * Core labels each lead itself (the "Core AI" labeler today; a second
 * labeler, Jev, will be added later) and the team can correct any lead.
 * The labels stay in Core for now: nothing is sent to WhatConverts or to
 * Google Ads. These helpers are the one place the page and the drawer
 * decide what "disagree", "corrected" and "right" mean, so both say the
 * same thing.
 */
import type {
  WcLabel,
  WcLead,
  WcLeadLabel,
  WcLeadLabelsMeta,
  WcQuotable,
} from '@/services/whatconvertsService';

export const AI_LABELER = 'ai_scorer';

/** Plain names for labelers; an unknown one shows as written. */
const LABELER_NAMES: Record<string, string> = {
  ai_scorer: 'Core AI',
  jev: 'Jev',
};

export function labelerName(labeler: string): string {
  return LABELER_NAMES[labeler] ?? labeler;
}

export const LABEL_TEXT: Record<WcLabel, string> = {
  yes: 'Quotable',
  no: 'Not quotable',
  pending: 'Pending',
};

export const LABEL_BADGE: Record<WcQuotable, string> = {
  yes: 'border-teal/30 bg-teal-light text-teal-dark',
  pending: 'border-amber/40 bg-amber-light text-amber-dark',
  no: 'border-coral/40 bg-coral-light text-coral-dark',
  not_set: 'border-border bg-muted/50 text-muted-foreground',
};

/** Labels are on and readable for this response. */
export function labelsReady(meta: WcLeadLabelsMeta | undefined): boolean {
  return Boolean(meta?.enabled && meta?.available);
}

/**
 * Labelers other than Core AI that have labelled at least one lead in this
 * response, in a stable order. Each gets its own column (Jev, later).
 */
export function extraLabelers(meta: WcLeadLabelsMeta | undefined): string[] {
  return (meta?.labelers ?? []).filter((l) => l !== AI_LABELER).sort();
}

export function labelFrom(lead: WcLead, labeler: string): WcLeadLabel | null {
  if (labeler === AI_LABELER && lead.ai_label !== undefined) return lead.ai_label ?? null;
  return lead.labels?.find((l) => l.labeler === labeler) ?? null;
}

/**
 * WhatConverts and Core AI both gave an answer and the answers differ.
 * A lead WhatConverts has not marked ("not set") is not a disagreement:
 * nobody answered on that side.
 */
export function isDisagreement(lead: WcLead): boolean {
  const ai = lead.ai_label?.label;
  if (!ai || lead.quotable === 'not_set') return false;
  return ai !== lead.quotable;
}

export function isCorrected(lead: WcLead): boolean {
  return Boolean(lead.correction);
}

/**
 * Against the team's correction: true = matches it, false = does not,
 * null = no correction yet (or this side gave no answer to compare).
 */
export function matchesTeam(lead: WcLead, answer: WcQuotable | WcLabel | null | undefined):
  | boolean
  | null {
  const truth = lead.correction?.label;
  if (!truth || !answer) return null;
  return answer === truth;
}

export type LeadLabelView = 'all' | 'disagree' | 'corrected';

export const LEAD_LABEL_VIEWS: readonly { value: LeadLabelView; label: string }[] = [
  { value: 'all', label: 'All leads' },
  { value: 'disagree', label: 'Disagreements' },
  { value: 'corrected', label: 'Corrected' },
];

export function applyLabelView(leads: WcLead[], view: LeadLabelView): WcLead[] {
  if (view === 'disagree') return leads.filter(isDisagreement);
  if (view === 'corrected') return leads.filter(isCorrected);
  return leads;
}

export function percent(rate: number | null | undefined): string {
  if (rate == null) return 'n/a';
  return `${Math.round(rate * 100)}%`;
}
